/**
 * Assemble + base64-encode the x402 `X-PAYMENT` header value.
 *
 * Shape is defined by the x402 spec v1 and consumed by the
 * facilitator's /verify + /settle endpoints (the backend's 402
 * Lambda proxies to it). Reference:
 * https://x402.org — `scheme: 'exact'`, `network` is the hyphenated
 * chain form (`base-mainnet`, `base-sepolia`, `ethereum-mainnet`…).
 *
 * The /info response returns `x402Network` in exactly that form so
 * we don't have to map anything — just pass through.
 */

/**
 * Build the payment-header payload object. `authorization` comes
 * from `buildTypedData(...).message`; `signature` is the 0x-prefixed
 * hex string returned by wagmi's `signTypedDataAsync`.
 */
export function buildX402PayloadObject(link, authorization, signature) {
  return {
    x402Version: 1,
    scheme: "exact",
    // Use x402Network (hyphenated form) — link.network on /info is
    // the Horus form (`BASE`), x402Network is the spec form
    // (`base-mainnet`). The facilitator only understands the latter.
    network: link.x402Network || link.network,
    payload: {
      signature,
      authorization,
    },
  };
}

/**
 * Base64-encode the payload object for the `X-PAYMENT` header.
 *
 * Browsers don't have Node's `Buffer`; `btoa` handles latin-1
 * strings. JSON output is ASCII so `btoa(JSON.stringify(...))` is
 * safe — no unicode surrogate pitfalls. Polyfills: `window.Buffer`
 * is set globally in App.js (line 56), but we stick to `btoa` to
 * avoid pulling in polyfill plumbing here.
 */
export function encodeX402Header(payloadObject) {
  const json = JSON.stringify(payloadObject);
  return btoa(json);
}

/**
 * Convenience one-shot — given the link + signed authorization,
 * return the `X-PAYMENT` header value ready to send.
 */
export function buildX402Header(link, authorization, signature) {
  return encodeX402Header(buildX402PayloadObject(link, authorization, signature));
}
